/* eslint "@typescript-eslint/no-restricted-imports": ["error", {
    "patterns": [
      {
        "group": ["*"],
        "message": "This file is used on the frontend, so try not to import extra code."
      }
    ]
  }
] */

// This is okay to import because it's just types and typeguards that the
// frontend also uses.
import type { ErrorOriginInfo } from "./error-origin-types";
import { PrivateErrorTypes } from "./errors-enums";

type DocumentLink = {
  label?: string;
  type: string;
  url?: string;
};

export type ErrorCodeDetails = {
  links?: DocumentLink[];
  userFriendlyMessage: string;
};

export enum SyncRequestErrorCode {
  /**
   * Returned if the specified model can not be executed which happens when two columns
   * have the same name.
   */
  DUPLICATE_COLUMNS_DETECTED = "DUPLICATE_COLUMNS_DETECTED",

  /**
   * Returned if the specified primary key isn't actually a primary key because it's not unique.
   * Not all planners require unique primary keys (e.g. `all` or `events` don't need it), but some
   * like in-warehouse do.
   */
  NON_UNIQUE_PRIMARY_KEY = "NON_UNIQUE_PRIMARY_KEY",

  /**
   * Returned if the specified primary key type is not supported bv warehouse planning
   */
  UNSUPPORTED_PRIMARY_KEY_TYPE = "UNSUPPORTED_PRIMARY_KEY_TYPE",

  /**
   * Returned if the objects from the most recent run of a sync can't be found.
   * This is most common when S3 plan objects expire (as of this writing, after 30 days).
   * When that happens, a full resync is needed.
   */
  PREVIOUS_SYNC_RUN_OBJECT_MISSING = "PREVIOUS_SYNC_RUN_OBJECT_MISSING",

  /**
   * Returned if the previous sync was a migration sync from the local differ to the
   * in-warehouse differ and there were some unsupported column types that
   * couldn't be backfilled.
   */
  REMOVE_PLAN_INCOMPLETE = "REMOVE_PLAN_INCOMPLETE",

  /**
   * Returned if the previous sync had rejected removes to retry, but the model
   * has changed the types of one or more columns.
   */
  REMOVE_RETRY_CHANGED_COLUMN_TYPES = "REMOVE_RETRY_CHANGED_COLUMN_TYPES",

  /**
   * Returned if we ran out of disk space when sorting rows.
   */
  SORT_RAN_OUT_OF_DISK_SPACE = "SORT_RAN_OUT_OF_DISK_SPACE",

  /**
   * Default error code for when there isn't a more specific one.
   */
  UNSPECIFIED = "UNSPECIFIED",

  /**
   * Returned if a table necessary for in-warehouse planning is not present.
   */
  WAREHOUSE_TABLE_MISSING = "WAREHOUSE_TABLE_MISSING",

  /**
   * Returned if a column in the query is missing from the warehouse.
   */
  QUERY_COLUMN_MISSING = "QUERY_COLUMN_MISSING",

  /**
   * Returned when an incremental sync is attempted after a prior successful sync
   * for an audience that uses exact count splits, which are not stable,
   * so the sync is blocked until a full resync is explicitly triggered.
   */
  EXACT_COUNT_INCREMENTAL_BLOCKED = "EXACT_COUNT_INCREMENTAL_BLOCKED",
}

export enum DestinationErrorCode {
  // Include "DESTINATION_" prefix to distinguish from source invalid grants.
  DESTINATION_INVALID_GRANT = "DESTINATION_INVALID_GRANT",
  DESTINATION_INVALID_CREDENTIALS = "DESTINATION_INVALID_CREDENTIALS",
  DESTINATION_INVALID_API_KEY = "DESTINATION_INVALID_API_KEY",

  // This is bad: the code for a given destination can't be found.
  DESTINATION_DEFINITION_NOT_FOUND = "DESTINATION_DEFINITION_NOT_FOUND",

  // Common Gateway Timeout error when destination server is not responding in a timely manner
  DESTINATION_INTERNAL_ERROR = "DESTINATION_INTERNAL_ERROR",

  DESTINATION_RATE_LIMIT = "DESTINATION_RATE_LIMIT",
  DESTINATION_MISSING_AD_ACCOUNT = "DESTINATION_MISSING_AD_ACCOUNT",
  DESTINATION_PAYLOAD_TOO_LARGE = "DESTINATION_PAYLOAD_TOO_LARGE",

  AMPLITUDE_INVALID_JSON_REQUEST = "AMPLITUDE_INVALID_JSON_REQUEST",

  CITRUS_AD_INVALID_BASE_URL = "CITRUS_AD_INVALID_BASE_URL",

  ITERABLE_INVALID_LIST_ID = "ITERABLE_INVALID_LIST_ID",

  SALESFORCE_INACTIVE_USER = "SALESFORCE_INACTIVE_USER",
  SALESFORCE_EXPIRED_TOKEN = "SALESFORCE_EXPIRED_TOKEN",
  SALESFORCE_INACTIVE_ORG = "SALESFORCE_INACTIVE_ORG",
  SALESFORCE_RESOURCE_DOES_NOT_EXIST = "SALESFORCE_RESOURCE_DOES_NOT_EXIST",
  SALESFORCE_AUTH_FAILURE = "SALESFORCE_AUTH_FAILURE",
  SALESFORCE_REQUESTS_LIMIT_EXCEEDED = "SALESFORCE_REQUESTS_LIMIT_EXCEEDED",
  SALESFORCE_NETWORK_DIFFICULTY = "SALESFORCE_NETWORK_DIFFICULTY",
  SALESFORCE_MAX_CALL_STACK = "SALESFORCE_MAX_CALL_STACK",
  SALESFORCE_UNABLE_TO_LOCK_ROW = "SALESFORCE_UNABLE_TO_LOCK_ROW",

  SFMC_DATA_EXTENSION_ADD_UPDATE_ONLY = "SFMC_DATA_EXTENSION_ADD_UPDATE_ONLY",
  SFMC_IMPORT_FAILED_NO_RECORDS = "SFMC_IMPORT_FAILED_NO_RECORDS",
  SFMC_INVALID_CLIENT_SECRET = "SFMC_INVALID_CLIENT_SECRET",

  GOOGLE_ADS_INTERNAL_SERVER_ERROR = "GOOGLE_ADS_INTERNAL_SERVER_ERROR",
  GOOGLE_ADS_USER_LIST_NOT_FOUND = "GOOGLE_ADS_USER_LIST_NOT_FOUND",
  GOOGLE_ADS_UNAUTHORIZED = "GOOGLE_ADS_UNAUTHORIZED",
  GOOGLE_ADS_USER_LIST_NAME_ALREADY_IN_USE = "GOOGLE_ADS_USER_LIST_NAME_ALREADY_IN_USE",
  GOOGLE_ADS_CONCURRENT_MODIFICATION = "GOOGLE_ADS_CONCURRENT_MODIFICATION",
  GOOGLE_ADS_STORE_SALES_DIRECT_DATA_NOT_ALLOWED = "GOOGLE_ADS_STORE_SALES_DIRECT_DATA_NOT_ALLOWED",
  GOOGLE_ADS_GENERIC_INVALID_ARGUMENT = "GOOGLE_ADS_GENERIC_INVALID_ARGUMENT",

  BRAZE_SERVICE_UNAVAILABLE = "BRAZE_SERVICE_UNAVAILABLE",
  BRAZE_ACCESS_DENIED = "BRAZE_ACCESS_DENIED",
  BRAZE_INVALID_REQUEST = "BRAZE_INVALID_REQUEST",

  TIKTOK_GATEWAY_TIMEOUT = "TIKTOK_GATEWAY_TIMEOUT",
  TIKTOK_REJECTED_REPLACE_OP = "TIKTOK_REJECTED_REPLACE_OP",
  TIKTOK_AUDIENCE_ERROR = "TIKTOK_AUDIENCE_ERROR",
  TIKTOK_PERMISSION_DENIED = "TIKTOK_PERMISSION_DENIED",
  TIKTOK_INVALID_ENCRYPTION_VALUE = "TIKTOK_INVALID_ENCRYPTION_VALUE",

  HUBSPOT_RATE_LIMIT_DAILY = "HUBSPOT_RATE_LIMIT_DAILY",
  HUBSPOT_RATE_LIMIT_TEN_SECONDLY = "HUBSPOT_RATE_LIMIT_TEN_SECONDLY",
  HUBSPOT_RATE_LIMIT_SECONDLY = "HUBSPOT_RATE_LIMIT_SECONDLY",
  HUBSPOT_INVALID_REFRESH_TOKEN = "HUBSPOT_INVALID_REFRESH_TOKEN",
  HUBSPOT_SYNC_ERROR = "HUBSPOT_SYNC_ERROR",
  HUBSPOT_CONNECTION_ERROR = "HUBSPOT_CONNECTION_ERROR",
  HUBSPOT_CONTACT_ALREADY_EXISTS = "HUBSPOT_CONTACT_ALREADY_EXISTS",
  HUBSPOT_CLOUDFLARE_BLOCKED_IP = "HUBSPOT_CLOUDFLARE_BLOCKED_IP",

  GOOGLE_SHEETS_INVALID_GRANT = "GOOGLE_SHEETS_INVALID_GRANT",
  GOOGLE_SHEETS_INVALID_DATA_TYPE = "GOOGLE_SHEETS_INVALID_DATA_TYPE",
  GOOGLE_SHEETS_INVALID_RANGE = "GOOGLE_SHEETS_INVALID_RANGE",
  GOOGLE_SHEETS_EXCEEDS_GRID_LIMITS = "GOOGLE_SHEETS_EXCEEDS_GRID_LIMITS",
  GOOGLE_SHEETS_MISSING_SHEET_ID = "GOOGLE_SHEETS_MISSING_SHEET_ID",
  GOOGLE_SHEETS_QUOTA_EXCEEDED = "GOOGLE_SHEETS_QUOTA_EXCEEDED",
  GOOGLE_SHEETS_ENTITY_NOT_FOUND = "GOOGLE_SHEETS_ENTITY_NOT_FOUND",

  SLACK_NOT_IN_CHANNEL = "SLACK_NOT_IN_CHANNEL_ERROR",
  SLACK_IS_ARCHIVED = "SLACK_IS_ARCHIVED_ERROR",
  SLACK_INVALID_BLOCKS = "SLACK_INVALID_BLOCKS_ERROR",
  SLACK_NO_TEXT = "SLACK_NO_TEXT_ERROR",
  SLACK_NON_CALLABLE_ITERATOR = "SLACK_NON_CALLABLE_ITERATOR_ERROR",
  SLACK_UNEXPECTED_TOKEN = "SLACK_UNEXPECTED_TOKEN_ERROR",

  ANAPLAN_BAD_MAPPINGS = "ANAPLAN_BAD_MAPPINGS",
  ANAPLAN_NULL_POINTER = "ANAPLAN_NULL_POINTER",

  TABOOLA_MISSING_NUMERIC_ACCOUNT_ID = "TABOOLA_MISSING_NUMERIC_ACCOUNT_ID",

  ELASTICSEARCH_INVALID_METADATA_TYPE = "ELASTICSEARCH_INVALID_METADATA_TYPE",

  FRESHDESK_CLOUDFLARE_IP_BLOCKED = "FRESHDESK_CLOUDFLARE_IP_BLOCKED",
}

export enum RejectedRowErrorCode {
  NULL_EXTERNAL_ID = "NULL_EXTERNAL_ID",
  DUPLICATE_PRIMARY_KEY = "DUPLICATE_PRIMARY_KEY",

  LINKEDIN_INVALID_CONVERSION = "LINKEDIN_INVALID_CONVERSION",
  LINKEDIN_EVENT_TOO_OLD = "LINKEDIN_EVENT_TOO_OLD",
  LINKEDIN_MISSING_USER_IDS = "LINKEDIN_MISSING_USER_IDS",
  LINKEDIN_MISSING_INVALID_USER_INFO = "LINKEDIN_MISSING_INVALID_USER_INFO",
  LINKEDIN_MISSING_IDENTIFIER_OR_NAME = "LINKEDIN_MISSING_IDENTIFIER_OR_NAME",
  LINKEDIN_NULL_OR_EMAIL_VALIDATION = "LINKEDIN_INVALID_SEGMENT_VALUE",
  LINKEDIN_PERMISSION_DENIED = "LINKEDIN_PERMISSION_DENIED",
  LINKEDIN_API_ERROR = "LINKEDIN_API_ERROR",
}

export enum ErrorMessages {
  USER_CANCELLATION = "Cancelled by user",
}

export interface BaseErrorInfo {
  /**
   * User-friendly details and instructions for the error. Generally written by Hightouch
   */
  userFriendlyMessage?: string;

  /**
   * Message to show to the end user. A sanitized version of the original error
   * message.
   */
  userFacingMessage?: string;

  /**
   * The original error message. Shown by default, but may contain sensitive
   * information. If userFacingMessage is set, this is not saved to the DB.
   * Often populated from `Error.prototype.message`.
   */
  message?: string;

  /**
   * Additional info retrieved from Sanity on the syncRequestErrorCode (i.e. links and userFriendlyMessage)
   */
  errorCodeDetail?: ErrorCodeDetails | null;
}

/**
 * SyncRequestErrorInfo is the data stored in `sync_request.error`.
 * All fields of SyncRequestErrorInfo are stored in the DB and exposed to the client,
 * so do NOT add anything sensitive to it.
 * It doesn't extend Error because it's a serialized JSON object, not an instance of Error.
 */
export interface SyncRequestErrorInfo extends BaseErrorInfo {
  // We call this `syncRequestErrorCode` instead of the terser `code` so that it doesn't conflict
  // with any other error codes that may be on an error cause chain.
  syncRequestErrorCode?:
    | SyncRequestErrorCode
    | DestinationErrorCode
    | RejectedRowErrorCode;

  /**
   * Info about the origin of the error. E.g., the error came from the source,
   * during the query operation, and it's not Hightouch's fault.
   */
  originInfo?: ErrorOriginInfo;
}

export interface RowLevelErrorInfo extends BaseErrorInfo {
  rowLevelErrorCode?: string;

  /**
   * The htId of the rejected row this error is for
   */
  rowId?: string;
}

export class DuplicateColumnsDetectedErrorInfo implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.DUPLICATE_COLUMNS_DETECTED;
}

export class NonUniquePrimaryKeyErrorInfo implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.NON_UNIQUE_PRIMARY_KEY;
  nonUniquePrimaryKeyInfo: {
    sqlToIdentifyDuplicateRows: string;
  };
  constructor(info: { sqlToIdentifyDuplicateRows: string }) {
    this.nonUniquePrimaryKeyInfo = info;
  }
}

export class UnsupportedPrimaryKeyTypeErrorInfo
  implements SyncRequestErrorInfo
{
  syncRequestErrorCode = SyncRequestErrorCode.UNSUPPORTED_PRIMARY_KEY_TYPE;
  supportedPrimaryKeyType: {
    strings: string;
    ints: string;
    floats: string;
  };
  type: string;
  constructor(info: {
    type: string;
    supportedPrimaryKeyType: {
      strings: string;
      ints: string;
      floats: string;
    };
  }) {
    this.supportedPrimaryKeyType = info.supportedPrimaryKeyType;
    this.type = info.type;
  }
}

export class PreviousSyncRunObjectMissing implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.PREVIOUS_SYNC_RUN_OBJECT_MISSING;
}

export class RemovePlanIncompleteErrorInfo implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.REMOVE_PLAN_INCOMPLETE;
  userFacingMessage?: string;
  constructor(info: { userFacingMessage: string }) {
    this.userFacingMessage = info.userFacingMessage;
  }
}

export class RemoveRetryChangedColumnTypes implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.REMOVE_RETRY_CHANGED_COLUMN_TYPES;
  changedColumns: string[];
  constructor(info: { changedColumns: string[] }) {
    this.changedColumns = info.changedColumns;
  }
}

export class SortRanOutOfDiskSpace implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.SORT_RAN_OUT_OF_DISK_SPACE;
}

export class WarehouseTableMissing implements SyncRequestErrorInfo {
  syncRequestErrorCode = SyncRequestErrorCode.WAREHOUSE_TABLE_MISSING;
  missingTables?: string[];
  constructor(info: { missingTables: string[] }) {
    this.missingTables = info.missingTables;
  }
}

export interface RejectedRowErrorInfo {
  rejectedRowErrorCode: string;
  scope?: "destination" | "source";
}

/**
 * RejectedRowError is a custom type used to signal that a sync failed due to some rows being rejected by the destination.
 */
export class SyncFailedWithRejectedRowsError extends Error {
  public readonly tag: string;

  static MESSAGE = "Some updates failed. See the syncs page for more details.";

  constructor() {
    super(SyncFailedWithRejectedRowsError.MESSAGE);
    this.tag = PrivateErrorTypes.SyncFailedWithRejectedRowsError;

    // Need to do this to get `instanceof` to work until we upgrade to ES2015 compile target.
    // TypeScript doesn't set the prototype when extending Error.
    Object.setPrototypeOf(this, SyncFailedWithRejectedRowsError.prototype);
  }

  public static isSyncFailedWithRejectedRowsError(err: Error | null): boolean {
    return Boolean(
      err &&
        (err instanceof SyncFailedWithRejectedRowsError ||
          (err as SyncFailedWithRejectedRowsError).tag ===
            PrivateErrorTypes.SyncFailedWithRejectedRowsError),
    );
  }
}

// .includes(MESSAGE) rather than exact equality, the same check merge.ts's
// treeHasError already uses. 30 days of production sync_attempts.error never
// truncate this message, they only ever carry it whole, sometimes with other
// text joined before or after it.
export function isRejectedRowsErrorMessage(
  message: string | undefined,
): boolean {
  return Boolean(message?.includes(SyncFailedWithRejectedRowsError.MESSAGE));
}

export type RetrySettings = (
  | { retryForever?: false; maxRetries: number }
  | { retryForever: true; maxRetries?: never }
) &
  (
    | {
        delayMs: number;
        initialDelayMs?: never;
        maxDelayMs?: never;
        incrementFactor?: never;
      }
    | {
        delayMs?: never;
        initialDelayMs: number;
        maxDelayMs?: number;
        incrementFactor: number;
      }
  );

/**
 * @param settings
 * @param retryNumber First retry (no increment) = 1, ...
 */
export function getDelay(settings: RetrySettings, retryNumber: number) {
  if ("delayMs" in settings) {
    return settings.delayMs;
  } else {
    const { initialDelayMs, incrementFactor, maxDelayMs } = settings;
    const delayMs = initialDelayMs * Math.pow(incrementFactor, retryNumber - 1);
    return maxDelayMs ? Math.min(delayMs, maxDelayMs) : delayMs;
  }
}

/**
 * RetryWithDelayError is an error type that needs to be retried with a backoff manner by worker
 */
export class RetryWithDelayError extends Error {
  public readonly retrySettings: RetrySettings;
  public readonly tag: string;

  constructor(error: Error, retrySettings: RetrySettings) {
    super(`Encountered error: ${error.message}. Retrying later`, {
      cause: error,
    });
    this.tag = PrivateErrorTypes.RetryWithDelayError;
    this.retrySettings = retrySettings;
  }

  public static isRetryError<T>(
    err: T,
  ): T extends RetryWithDelayError ? true : false {
    if (!err || typeof err !== "object") {
      // @ts-expect-error
      return false;
    }

    // @ts-expect-error
    return "message" in err && "stack" in err && "retrySettings" in err;
  }
}

export function extractRetryError(
  error: Error | RetryWithDelayError | null | undefined,
): RetryWithDelayError | null {
  while (
    error &&
    !RetryWithDelayError.isRetryError(error) &&
    error.cause &&
    typeof error.cause === "object"
  ) {
    error = error.cause as Error | RetryWithDelayError;
  }

  return RetryWithDelayError.isRetryError(error)
    ? (error as RetryWithDelayError)
    : null;
}

const unRetriableKey = Symbol("un-retriable");
export function unRetriable<T extends Error>(err: T): T {
  return Object.defineProperty(err, unRetriableKey, {
    value: true,
    enumerable: false,
    configurable: false,
  });
}

export function isRetryForbidden(error?: Error | null): boolean {
  while (
    error &&
    !error[unRetriableKey] &&
    error.cause &&
    typeof error.cause === "object"
  ) {
    error = error.cause as Error | RetryWithDelayError;
  }

  return Boolean(error?.[unRetriableKey]);
}
